// =============================================================================
// Super-admin SaaS dashboard API helpers (read-only). Admin token required.
// =============================================================================
import { BASE_URL } from "../../data/BaseUrl";

const authGet = async (path, token) => {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
};

// GET /saas/admin/overview
// market: "IN" | "US" — the console shows one market at a time (India ₹ | US $)
const mq = (market) => (market ? `market=${encodeURIComponent(market)}` : "");
export const fetchSaasOverview = (token, market) => authGet(`/saas/admin/overview${market ? `?${mq(market)}` : ""}`, token);

// GET/PUT /saas/admin/tax-config — configurable owner-billing + India member rates
export const fetchTaxConfig = (token) => authGet(`/saas/admin/tax-config`, token);
export const updateTaxConfig = async (payload, token) => {
  const res = await fetch(`${BASE_URL}/saas/admin/tax-config`, {
    method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  return res.json();
};

// GET /saas/admin/owners (filters as a plain object)
export const fetchSaasOwners = (filters, token) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(filters || {}).filter(([, v]) => v !== "" && v != null))
  ).toString();
  return authGet(`/saas/admin/owners${qs ? `?${qs}` : ""}`, token);
};

// GET /saas/admin/owners/:uid/overview — per-owner drill-down
export const fetchSaasOwnerOverview = (uid, token) => authGet(`/saas/admin/owners/${uid}/overview`, token);

// GET /saas/admin/plan-distribution
export const fetchSaasPlanDistribution = (token, market) => authGet(`/saas/admin/plan-distribution${market ? `?${mq(market)}` : ""}`, token);

// GET /saas/admin/revenue-trend?range=30d|12m
export const fetchSaasRevenueTrend = (range, token, market) =>
  authGet(`/saas/admin/revenue-trend?range=${range || "30d"}${market ? `&${mq(market)}` : ""}`, token);

// GET /saas/admin/metrics — ARPU / churn / LTV / MRR-movement + snapshot history
export const fetchSaasMetrics = (token, market) => authGet(`/saas/admin/metrics${market ? `?${mq(market)}` : ""}`, token);

// ---- Phase 2: per-owner admin ACTIONS (write, admin-guarded) ---------------
const authPost = async (path, body, token) => {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  return res.json();
};
// POST /saas/admin/owners/:uid/status  { status: 'active' | 'inactive' | 'archived' }
export const setSaasOwnerStatus = (uid, status, token) => authPost(`/saas/admin/owners/${uid}/status`, { status }, token);
// POST /saas/admin/owners/:uid/kyc    { kyc_status: 'verified' | 'rejected' | 'pending' }
export const setSaasOwnerKyc = (uid, kyc_status, token) => authPost(`/saas/admin/owners/${uid}/kyc`, { kyc_status }, token);
// POST /saas/admin/owners/:uid/extend { days: 1..365 }
// WGO-F008 — `reason` is not optional: the server refuses an extension without
// one, because a grant of free service that carries no justification is
// indistinguishable in the audit trail from a misclick.
export const extendSaasOwnerSub = (uid, days, token, reason) => authPost(`/saas/admin/owners/${uid}/extend`, { days, reason }, token);
// POST /saas/admin/owners/:uid/limit { max_onboarding_gyms: 0..999 }
export const setSaasOwnerLimit = (uid, max_onboarding_gyms, token) => authPost(`/saas/admin/owners/${uid}/limit`, { max_onboarding_gyms }, token);
// POST /saas/admin/owners/:uid/crm { next_followup_at?, followup_status?, tags?, health?, assigned_csm_uid?, assigned_csm_name? }
export const setSaasOwnerCrm = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/crm`, payload, token);
// POST /saas/admin/owners/:uid/note { body }
export const addSaasOwnerNote = (uid, body, token) => authPost(`/saas/admin/owners/${uid}/note`, { body }, token);
// POST /saas/admin/owners/:uid/sub-status { subscription_status: 'active' | 'cancelled' }
export const setSaasOwnerSubStatus = (uid, subscription_status, token) => authPost(`/saas/admin/owners/${uid}/sub-status`, { subscription_status }, token);
// POST /saas/admin/owners/:uid/plan { saas_plan_id, billing_cycle? }
export const changeSaasOwnerPlan = (uid, saas_plan_id, billing_cycle, token) => authPost(`/saas/admin/owners/${uid}/plan`, { saas_plan_id, billing_cycle }, token);
// GET /saas/plan/all — plan catalog (for the change-plan picker)
export const fetchSaasPlansAll = (token) => authGet(`/saas/plan/all`, token);

// ---- What's New (product updates) authoring (admin-guarded) -----------------
export const fetchWhatsNewAdmin = (token) => authGet(`/saas/admin/whats-new`, token);
export const getWhatsNewAdmin = (uid, token) => authGet(`/saas/admin/whats-new/${uid}`, token);
export const previewWhatsNewAdmin = (uid, token) => authGet(`/saas/admin/whats-new/${uid}/preview`, token);
export const createWhatsNewAdmin = (payload, token) => authPost(`/saas/admin/whats-new`, payload, token);
export const publishWhatsNewAdmin = (uid, token) => authPost(`/saas/admin/whats-new/${uid}/publish`, {}, token);
export const unpublishWhatsNewAdmin = (uid, token) => authPost(`/saas/admin/whats-new/${uid}/unpublish`, {}, token);
export const updateWhatsNewAdmin = async (uid, payload, token) => {
  const res = await fetch(`${BASE_URL}/saas/admin/whats-new/${uid}`, {
    method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  return res.json();
};
export const deleteWhatsNewAdmin = async (uid, token) => {
  const res = await fetch(`${BASE_URL}/saas/admin/whats-new/${uid}`, {
    method: "DELETE", headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
};

// WTF-team superadmin login (PUBLIC — no token). Returns { status, data:{ token, account_type, ... } }.
export const saasAdminLogin = async (username, password) => {
  const res = await fetch(`${BASE_URL}/saas/admin/login`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  return res.json();
};

// GET /saas/admin/features?status=&scope= — dashboard feature catalog for the plan wizard
// (replaces the legacy bare /admin catalog call that the SaaS backend doesn't serve).
// scope: "owner" → owner-portal (/app) features only; omitted → the whole catalogue.
export const fetchPlanFeatures = (filters, token) => {
  const p = new URLSearchParams();
  if (filters && filters.status != null) p.set("status", filters.status);
  if (filters && filters.scope) p.set("scope", filters.scope);
  const q = p.toString();
  return authGet(`/saas/admin/features${q ? `?${q}` : ""}`, token);
};

// ---- Phase B: platform data lists (read-only). Each returns ---------------
//   { status, data:[rows], pagination:{page,limit,total,pages}, stats:{...} }
const qs = (filters) =>
  new URLSearchParams(
    Object.fromEntries(Object.entries(filters || {}).filter(([, v]) => v !== "" && v != null))
  ).toString();
const listGet = (base) => (filters, token) => {
  const q = qs(filters);
  return authGet(`${base}${q ? `?${q}` : ""}`, token);
};
// ---- Feature monetization (free / plan / add-on classifier) ----------------
export const fetchMonetization = (filters, token) => {
  const q = qs(filters);
  return authGet(`/saas/admin/monetization${q ? `?${q}` : ""}`, token);
};
export const saveMonetization = async (featureId, payload, token) => {
  const res = await fetch(`${BASE_URL}/saas/admin/monetization/${featureId}`, {
    method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  return res.json();
};
export const clearMonetization = async (featureId, token) => {
  const res = await fetch(`${BASE_URL}/saas/admin/monetization/${featureId}`, {
    method: "DELETE", headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
};

export const fetchSaasSubscriptions = listGet("/saas/admin/subscriptions");
// GET /saas/admin/collect — every amount an owner still owes (Owner Payments → To collect)
export const fetchCollect = listGet("/saas/admin/collect");
// POST /saas/admin/collect/checkout/:uid/write-off { reason } — super admin
export const writeOffCheckout = (uid, reason, token) => authPost(`/saas/admin/collect/checkout/${encodeURIComponent(uid)}/write-off`, { reason }, token);
// Payment links — every WTF payment link sent to an owner (who made it, open / paid, how)
export const fetchPaymentLinks = listGet("/saas/admin/payment-links");
// POST /saas/admin/payment-links/:linkId/check — ask Razorpay about any link; apply if paid
export const checkPaymentLink = (linkId, token) => authPost(`/saas/admin/payment-links/${encodeURIComponent(linkId)}/check`, {}, token);
export const fetchSaasTransactions = listGet("/saas/admin/transactions");
export const fetchSaasGyms = listGet("/saas/admin/gyms");
// Cross-entity ⌘K global search — GET /saas/admin/search?q=
export const fetchSaasSearch = (q, token) => authGet(`/saas/admin/search?q=${encodeURIComponent(q || "")}`, token);

// ---- WTF→owner subscription invoices (Owner Billing console) ----------------
export const fetchOwnerInvoices = listGet("/saas/admin/owner-invoices");
export const fetchOwnerInvoice = (subUid, token) => authGet(`/saas/admin/owner-invoices/${subUid}`, token);
export const issueOwnerInvoice = (subUid, token) => authPost(`/saas/admin/owner-invoices/${subUid}/issue`, {}, token);
// every payment with no invoice yet (after Seller settings are filled in)
export const issuePendingOwnerInvoices = (token) => authPost("/saas/admin/owner-invoices/issue-pending", {}, token);
// email an issued invoice to the owner again — or to another address ({ email })
export const sendOwnerInvoice = (subUid, body, token) => authPost(`/saas/admin/owner-invoices/${subUid}/send`, body || {}, token);
// a credit note against an issued owner invoice: { amount? (blank = what is left), reason }
export const creditOwnerInvoice = (subUid, body, token) => authPost(`/saas/admin/owner-invoices/${subUid}/credit-note`, body || {}, token);
// Platform seller identity (WTF legal name / GSTIN / state) config
export const fetchPlatformSeller = (token) => authGet(`/saas/admin/platform-seller`, token);
export const updatePlatformSeller = (payload, token) => authPut(`/saas/admin/platform-seller`, payload, token);
// WTF's logo on its invoices (534): multipart "logo" — PNG / JPG / WebP, ≤ 2 MB → { status, seller } | { status:false, message, errors }
export const uploadPlatformSellerLogo = async (file, token) => {
  try {
    const fd = new FormData();
    fd.append("logo", file);
    const res = await fetch(`${BASE_URL}/saas/admin/platform-seller/logo`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
    return await res.json();
  } catch (e) { return { status: false, message: "The logo couldn't be uploaded — check your connection and try again" }; }
};

// ---- Team & Access (phase 1: read only) ------------------------------------
// The admin roster. Mutations land in phase 2 behind requireSuperAdmin.
export const fetchSaasTeam = listGet("/saas/admin/team");
// Who am I, at what level. The role is deliberately NOT in the JWT — the server
// re-reads it per request so a revoked admin is locked out immediately — so the
// client asks for it rather than decoding a token.
export const fetchAdminMe = (token) => authGet("/saas/admin/me", token);
// The topbar bell (535): my own notifications — { items, unread }; mark { uids } or { all: true } read
export const fetchAdminInbox = (token, limit = 20) => authGet(`/saas/admin/inbox?limit=${limit}`, token);
export const markAdminInboxRead = (payload, token) => authPost("/saas/admin/inbox/read", payload, token);

// ---- Team & Access mutations (phase 2, super admin only) --------------------
const authPut = async (path, body, token) => {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  return res.json();
};
export const fetchAdminInvites = (token) => authGet("/saas/admin/team/invites", token);
export const inviteAdmin = (payload, token) => authPost("/saas/admin/team/invite", payload, token);
export const revokeAdminInvite = (uid, token) => authPost(`/saas/admin/team/invites/${uid}/revoke`, {}, token);
export const setAdminRole = (uid, admin_role, token) => authPut(`/saas/admin/team/${uid}/role`, { admin_role }, token);
export const setAdminStatus = (uid, status, token) => authPut(`/saas/admin/team/${uid}/status`, { status }, token);
export const resetAdminPassword = (uid, token) => authPost(`/saas/admin/team/${uid}/reset`, {}, token);
// super admin types a new password for another admin; an admin changes their own
export const setAdminPassword = (uid, password, token) => authPost(`/saas/admin/team/${uid}/password`, { password }, token);
export const changeMyAdminPassword = (current_password, new_password, token) =>
  authPost("/saas/admin/me/password", { current_password, new_password }, token);

// ---- Roles & permissions (phase 3, super admin only) -----------------------
const authDelete = async (path, token) =>
  (await fetch(`${BASE_URL}${path}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })).json();

export const fetchAdminRoles = (token) => authGet("/saas/admin/roles", token);
export const fetchAdminPermissions = (token) => authGet("/saas/admin/roles/permissions", token);
export const createAdminRole = (payload, token) => authPost("/saas/admin/roles", payload, token);
export const updateAdminRole = (uid, payload, token) => authPut(`/saas/admin/roles/${uid}`, payload, token);
export const archiveAdminRole = (uid, token) => authDelete(`/saas/admin/roles/${uid}`, token);
// role_uid: null puts the admin back on full (legacy) access.
export const assignAdminRole = (uid, role_uid, token) =>
  authPut(`/saas/admin/team/${uid}/permission-role`, { role_uid }, token);

// ---- PUBLIC: redeeming an invite / reset link (no session yet) --------------
// Deliberately NOT using authGet/authPost — the holder of the token has no token
// of the other kind, and sending an Authorization header here would be noise.
export const inspectAdminInvite = async (t) =>
  (await fetch(`${BASE_URL}/saas/admin/invite/${encodeURIComponent(t)}`)).json();
export const acceptAdminInvite = async (t, password) =>
  (await fetch(`${BASE_URL}/saas/admin/invite/${encodeURIComponent(t)}/accept`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  })).json();
export const fetchSaasNotifications = listGet("/saas/admin/notifications");
export const fetchSaasActivity = listGet("/saas/admin/activity");

// ---- Owner onboarding (staff-led pipeline, /admin/onboarding) --------------
export const fetchOnboarding = listGet("/saas/admin/onboarding");
// owner (optional): a lead that already has a login also sees that owner's custom plans
export const fetchOnboardingPlans = (token, owner, market, zip, state) => {
  const q = new URLSearchParams();
  if (owner) q.set("owner", owner);
  if (market) q.set("market", market);   // the buyer's tax comes back as `tax` (user 2026-10-27)
  if (zip) q.set("zip", zip);            // a US lead's ZIP, typed before the login exists
  if (state) q.set("state", state);
  const qs = q.toString();
  return authGet(`/saas/admin/onboarding/plans${qs ? `?${qs}` : ""}`, token);
};
// choose / change the plan AFTER the login exists (custom-plan path)
export const chooseOnboardingPlan = (uid, payload, token) => authPost(`/saas/admin/onboarding/${uid}/plan`, payload, token);
export const fetchOnboardingLead = (uid, token) => authGet(`/saas/admin/onboarding/${uid}`, token);
export const createOnboardingLead = (payload, token) => authPost("/saas/admin/onboarding", payload, token);
export const updateOnboardingLead = (uid, payload, token) => authPut(`/saas/admin/onboarding/${uid}`, payload, token);
export const moveOnboardingStage = (uid, payload, token) => authPost(`/saas/admin/onboarding/${uid}/stage`, payload, token);
export const createOnboardingAccount = (uid, payload, token) => authPost(`/saas/admin/onboarding/${uid}/account`, payload, token);
export const sendOnboardingPaymentLink = (uid, token) => authPost(`/saas/admin/onboarding/${uid}/payment-link`, {}, token);
// email the CURRENT link again — no new Razorpay link; { email } sends it to another address
export const resendOnboardingLinkEmail = (uid, email, token) => authPost(`/saas/admin/onboarding/${uid}/payment-link/email`, email ? { email } : {}, token);
// GET — what the owner gets with the link (proforma) + the tax invoice after payment; read-only
export const previewOnboardingInvoice = (uid, token) => authGet(`/saas/admin/onboarding/${uid}/invoice-preview`, token);
export const checkOnboardingPayment = (uid, token) => authPost(`/saas/admin/onboarding/${uid}/check-payment`, {}, token);
export const markOnboardingCash = (uid, payload, token) => authPost(`/saas/admin/onboarding/${uid}/cash`, payload, token);
// the owner's account manager, picked by an admin (2026-11-03 — never set by itself on payment); null = nobody
export const setOnboardingAccountManager = (uid, adminUid, token) => authPut(`/saas/admin/onboarding/${uid}/account-manager`, { admin_uid: adminUid || null }, token);

// ---- Owner details / password (admin, owners.write) ------------------------
export const updateSaasOwnerProfile = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/profile`, payload, token);
export const resetSaasOwnerPassword = (uid, password, token) => authPost(`/saas/admin/owners/${uid}/password`, { password }, token);

// ---- Support access (super admin / owners.support_access) -----------------
// POST start → { token, user, session }; the console opens /support/enter#t=<token>.
export const startSupportSession = (uid, token) => authPost(`/saas/admin/owners/${encodeURIComponent(uid)}/support-session`, {}, token);
export const endSupportSessionAdmin = (sid, token) => authPost(`/saas/admin/support-sessions/${encodeURIComponent(sid)}/end`, {}, token);
export const fetchSupportSessions = (uid, token) => authGet(`/saas/admin/owners/${encodeURIComponent(uid)}/support-sessions`, token);

// ---- Assign plan + expiry control (admin, owners.billing) -------------------
// POST /saas/admin/owners/:uid/assign-plan
//   { plan_uid, billing_cycle?, fee?, max_gyms?, note?, expiry: { mode, days?, date? },
//     payment: 'link'|'cash'|'none', cash_reference? }
export const assignSaasOwnerPlan = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/assign-plan`, payload, token);
export const checkSaasAssignPayment = (uid, aid, token) => authPost(`/saas/admin/owners/${uid}/assign-plan/${aid}/check-payment`, {}, token);
export const fetchSaasOwnerAssignments = (uid, token) => authGet(`/saas/admin/owners/${uid}/assignments`, token);
// POST /saas/admin/owners/:uid/expiry { mode: 'date'|'days'|'none'|'now', date?, days?, reason }
export const setSaasOwnerExpiry = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/expiry`, payload, token);

// ---- Manage plan hub: everything about one owner's plan (controller saasAdminPlanHub)
export const fetchPlanHub = (uid, token) => authGet(`/saas/admin/owners/${uid}/plan-hub`, token);
// { plan_uid, billing_cycle? } → what the change costs now (difference for the days left)
export const previewPlanChange = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/plan-change/preview`, payload, token);
// { plan_uid, billing_cycle?, amount?, reason, payment: 'link'|'cash'|'none', cash_reference? }
export const changeOwnerPlan = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/plan-change`, payload, token);
export const checkPlanChangePayment = (uid, cid, token) => authPost(`/saas/admin/owners/${uid}/plan-change/${cid}/check-payment`, {}, token);
export const cancelPlanChange = (uid, cid, token) => authPost(`/saas/admin/owners/${uid}/plan-change/${cid}/cancel`, {}, token);
// the plan chosen at account creation (Onboarding), collected from Manage plan
export const onboardingPayLink = (uid, token) => authPost(`/saas/admin/owners/${uid}/onboarding-pay/link`, {}, token);
export const onboardingPayCheck = (uid, token) => authPost(`/saas/admin/owners/${uid}/onboarding-pay/check`, {}, token);
export const onboardingPayCash = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/onboarding-pay/cash`, payload, token);
// { source_plan_uid, pricing?, gst_type?, plan_name? } → a custom plan of this owner
export const forkOwnerPlan = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/fork-plan`, payload, token);
export const pauseOwnerPlan = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/pause`, payload, token);
export const resumeOwnerPlan = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/resume`, payload, token);
// { caps: { field: number | null }, reason }
export const setOwnerCaps = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/caps`, payload, token);
// { feature_id, verdict: 'allow'|'deny', reason, expires_at? }
export const setOwnerFeatureOverride = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/feature-overrides`, payload, token);
export const removeOwnerFeatureOverride = async (uid, fid, token) =>
  (await fetch(`${BASE_URL}/saas/admin/owners/${uid}/feature-overrides/${encodeURIComponent(fid)}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })).json();
// { feature_id, action: 'grant'|'extend'|'cancel', days?, reason }
export const ownerAddonAction = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/addons`, payload, token);

// ---- Admin bills: plan + add-on services, in full or installments (owners.billing)
// POST /saas/admin/owners/:uid/bills/preview { plan?, addons?, installments_count? }
export const previewOwnerBill = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/bills/preview`, payload, token);
// POST /saas/admin/owners/:uid/bills { plan?, addons?, schedule, plan_start?, reminders?, auto_lock_days?, note? }
export const createOwnerBill = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/bills`, payload, token);
// POST /saas/admin/owners/:uid/bills/invoice-preview — same body as createOwnerBill; read-only:
// the proforma email + the tax invoice (no number) the owner would get, and warnings
// POST — the changePlan body; read-only: what the owner gets for the difference
export const previewPlanChangeInvoice = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/plan-change/invoice-preview`, payload, token);
export const previewOwnerBillInvoice = (uid, payload, token) => authPost(`/saas/admin/owners/${uid}/bills/invoice-preview`, payload, token);
export const fetchOwnerBills = (uid, token) => authGet(`/saas/admin/owners/${uid}/bills`, token);
// GET /saas/admin/owners/:uid/tax — the tax this owner pays: { rate, known, label, currency }
export const fetchOwnerTax = (uid, token) => authGet(`/saas/admin/owners/${encodeURIComponent(uid)}/tax`, token);
// GET /saas/admin/bills?status=&search=&market=&page= (kit list shape)
export const fetchBillsList = (filters, token) => listGet("/saas/admin/bills")(filters, token);
export const fetchBill = (uid, token) => authGet(`/saas/admin/bills/${encodeURIComponent(uid)}`, token);
export const saveBillSchedule = (uid, payload, token) => authPut(`/saas/admin/bills/${encodeURIComponent(uid)}/schedule`, payload, token);
export const sendInstallmentLink = (uid, iid, token) => authPost(`/saas/admin/bills/${encodeURIComponent(uid)}/installments/${encodeURIComponent(iid)}/link`, {}, token);
export const checkInstallmentPayment = (uid, iid, token) => authPost(`/saas/admin/bills/${encodeURIComponent(uid)}/installments/${encodeURIComponent(iid)}/check-payment`, {}, token);
// { mode: cash|upi|bank|cheque, reference, paid_on?, amount? }
export const markInstallmentPaid = (uid, iid, payload, token) => authPost(`/saas/admin/bills/${encodeURIComponent(uid)}/installments/${encodeURIComponent(iid)}/mark-paid`, payload, token);
export const waiveInstallment = (uid, iid, reason, token) => authPost(`/saas/admin/bills/${encodeURIComponent(uid)}/installments/${encodeURIComponent(iid)}/waive`, { reason }, token);
// { reminders?, auto_lock_days?, locked? }
export const saveBillSettings = (uid, payload, token) => authPost(`/saas/admin/bills/${encodeURIComponent(uid)}/settings`, payload, token);
// POST /saas/admin/bills/:uid/addons { addons, collect: first|last|new, due_date? } — merge add-ons into an unpaid bill
export const addBillAddons = (uid, payload, token) => authPost(`/saas/admin/bills/${encodeURIComponent(uid)}/addons`, payload, token);
export const cancelBill = (uid, reason, token) => authPost(`/saas/admin/bills/${encodeURIComponent(uid)}/cancel`, { reason }, token);

// ---- Add-on services price list (plans.read / plans.write) -------------------
export const fetchServiceAddons = (token, status = "active") => authGet(`/saas/admin/service-addons?status=${encodeURIComponent(status)}`, token);
export const createServiceAddon = (payload, token) => authPost(`/saas/admin/service-addons`, payload, token);
export const updateServiceAddon = (uid, payload, token) => authPut(`/saas/admin/service-addons/${encodeURIComponent(uid)}`, payload, token);

// ---- Copy a custom plan for another owner (plans.write) ----------------------
// GET  /saas/plan/copy-sources?owner= — other owners' custom plans in this owner's currency
// POST /saas/plan/:uid/copy { owner_user_id, plan_name?, price?, price_annual? }
export const fetchPlanCopySources = (owner, token) =>
  authGet(`/saas/plan/copy-sources?owner=${encodeURIComponent(owner)}`, token);
export const copySaasPlan = (planUid, payload, token) => authPost(`/saas/plan/${planUid}/copy`, payload, token);

// ---- Delete (test data only) + 30-day undo; archive real owners ---------------
// A lead without a login (onboarding.write)
export const deleteOnboardingLead = (uid, token) => authPost(`/saas/admin/onboarding/${uid}/delete`, {}, token);
// A test owner — SUPER ADMIN only. check → { deletable, blockers:[{code,label,count}], will_delete, email }
export const checkSaasOwnerDelete = (uid, token) => authGet(`/saas/admin/owners/${uid}/delete-check`, token);
export const deleteSaasOwner = (uid, confirm_email, token) => authPost(`/saas/admin/owners/${uid}/delete`, { confirm_email }, token);
// "Recently deleted" (owners, leads, plans) — SUPER ADMIN only
export const fetchRecentlyDeleted = (token) => authGet("/saas/admin/deleted", token);
export const restoreDeleted = (id, token) => authPost(`/saas/admin/deleted/${id}/restore`, {}, token);

// ---- PUBLIC: admin "Forgot password?" (BUG-25) -------------------------------
// POST /saas/admin/password/forgot { email } — no session, so no Authorization
// header. The server answers the same way whether or not the email is an admin;
// the caller must show that answer as-is and never infer anything from it.
export const saasAdminForgotPassword = async (email) =>
  (await fetch(`${BASE_URL}/saas/admin/password/forgot`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  })).json();

// ---- money recorded by hand: proof files + cash approvals (backend saasOfflinePayment) ----
// POST /saas/admin/payment-proofs (multipart "files") → [{ uid, name, type, size }]
export const uploadPaymentProofs = async (files, token) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  const res = await fetch(`${BASE_URL}/saas/admin/payment-proofs`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
  return res.json();
};
// GET /saas/admin/payment-proofs/:fid — private file → an object URL to show / open
export const paymentProofUrl = async (fid, token) => {
  const res = await fetch(`${BASE_URL}/saas/admin/payment-proofs/${encodeURIComponent(fid)}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("not found");
  return URL.createObjectURL(await res.blob());
};
// market (IN | US) scopes the list to that currency; left out = every currency
export const fetchCashApprovals = (status, token, market) => authGet(`/saas/admin/cash-approvals?status=${encodeURIComponent(status || "pending")}${market ? `&market=${encodeURIComponent(market)}` : ""}`, token);
export const fetchCashApprovalSettings = (token) => authGet(`/saas/admin/cash-approvals/settings`, token);
export const setCashApprovalSettings = (enabled, token) => authPost(`/saas/admin/cash-approvals/settings`, { enabled }, token);
export const approveCashPayment = (uid, token) => authPost(`/saas/admin/cash-approvals/${uid}/approve`, {}, token);
export const rejectCashPayment = (uid, reason, token) => authPost(`/saas/admin/cash-approvals/${uid}/reject`, { reason }, token);
